<template>
  <div class="ts-view">
    <TechnicianTopBar
      :title="item ? 'Mantenimiento' : 'Detalle'"
      :priority="item ? { label: INFRA_LABELS[item.infraType], color: item.color } : null"
      back
      @back="goBack"
    />

    <div v-if="loading" class="ts-state"><span class="mdi mdi-loading ts-state__spinner"></span></div>

    <div v-else-if="loadError" class="ts-state ts-state--message">
      <span class="mdi mdi-alert-circle-outline ts-state__icon"></span>
      <p>{{ loadError }}</p>
      <button class="ts-btn ts-btn--outline" @click="goBack">Volver al calendario</button>
    </div>

    <div v-else class="ts-body">
      <div class="ts-column">
        <section class="ts-block">
          <h1 class="ts-title">{{ item.title }}</h1>
          <span class="ts-status" :style="{ backgroundColor: statusInfo.color }">{{ statusInfo.label }}</span>
          <p v-if="item.description" class="ts-description">{{ item.description }}</p>
        </section>

        <section class="ts-block ts-fields">
          <div class="ts-field"><span class="ts-field__label">Fecha</span><span>{{ formatFull(item.startAt) }}</span></div>
          <div class="ts-field"><span class="ts-field__label">Hora</span><span>{{ timeOf(item.startAt) }}</span></div>
          <div v-if="item.durationHours" class="ts-field"><span class="ts-field__label">Duración estimada</span><span>{{ formatDuration(item.durationHours) }}</span></div>
          <div class="ts-field"><span class="ts-field__label">Zona</span><span>{{ item.zoneName || '—' }}</span></div>
          <div class="ts-field"><span class="ts-field__label">Nave</span><span>{{ item.plotName || '—' }}</span></div>
          <div v-if="item.isRecurring" class="ts-field"><span class="ts-field__label">Frecuencia</span><span>{{ FREQUENCY_LABELS[item.frequency] }}</span></div>
        </section>

        <!-- Ya completado: se muestra lo que se reportó -->
        <section v-if="item.status === 'COMPLETED'" class="ts-block">
          <div class="ts-label">Trabajo realizado</div>
          <p class="ts-description">{{ item.completionNotes || 'Sin descripción.' }}</p>
          <img
            v-if="item.completionPhotoPath"
            :src="photoSrc(item.completionPhotoPath)"
            class="ts-photo"
            alt="Foto del trabajo"
            @click="lightbox = photoSrc(item.completionPhotoPath)"
          />
          <div v-if="item.completedAt" class="ts-done-date">Completado el {{ formatShortDateTime(item.completedAt) }}</div>
        </section>

        <!-- Única acción disponible para el técnico -->
        <section v-else class="ts-block">
          <template v-if="!completing">
            <button class="ts-btn ts-btn--primary ts-btn--block" @click="completing = true">Marcar como completado</button>
          </template>
          <template v-else>
            <label class="ts-field-form">
              <span class="ts-field-form__label">Describe el trabajo realizado <span class="ts-required">*</span></span>
              <textarea v-model="description" class="ts-textarea" rows="4" placeholder="Qué se revisó o se hizo, observaciones..."></textarea>
            </label>

            <input ref="fileInput" type="file" accept="image/*" capture="environment" class="ts-hidden" @change="onFileSelected" />
            <div class="ts-attach">
              <button class="ts-btn ts-btn--outline" type="button" @click="fileInput?.click()">
                <span class="mdi mdi-camera-outline"></span>&nbsp;{{ photoFile ? 'Cambiar foto' : 'Adjuntar foto del trabajo' }}
              </button>
              <span v-if="photoFile" class="ts-attach__name">{{ photoFile.name }}</span>
            </div>

            <div v-if="completeError" class="ts-error">{{ completeError }}</div>
            <div class="ts-actions">
              <button class="ts-btn ts-btn--outline ts-btn--half" :disabled="saving" @click="cancelCompleting">Cancelar</button>
              <button class="ts-btn ts-btn--primary ts-btn--half" :disabled="saving || !description.trim()" @click="onComplete">
                {{ saving ? 'Guardando…' : 'Confirmar' }}
              </button>
            </div>
          </template>
        </section>
      </div>
    </div>

    <div v-if="lightbox" class="ts-lightbox" @click="lightbox = ''">
      <img :src="lightbox" alt="" />
      <button class="ts-lightbox__close" aria-label="Cerrar"><span class="mdi mdi-close"></span></button>
    </div>

    <div v-if="toast" class="ts-toast"><span class="mdi mdi-check-circle"></span> {{ toast }}</div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import TechnicianTopBar from '@/components/Technician/TechnicianTopBar.vue'
import {
  getMyScheduledDetail, completeMyScheduled,
  INFRA_LABELS, FREQUENCY_LABELS, SCHEDULED_STATUS, formatDuration, formatShortDateTime, photoSrc,
} from '@/services/technicianPortal'

const route = useRoute()
const router = useRouter()
const scheduledId = computed(() => route.params.id)

const item = ref(null)
const loading = ref(true)
const loadError = ref('')
const lightbox = ref('')

const completing = ref(false)
const description = ref('')
const photoFile = ref(null)
const fileInput = ref(null)
const saving = ref(false)
const completeError = ref('')

const toast = ref('')
let toastTimer = null
function showToast(message) {
  toast.value = message
  clearTimeout(toastTimer)
  toastTimer = setTimeout(() => { toast.value = '' }, 3000)
}

const statusInfo = computed(() => SCHEDULED_STATUS[item.value?.status] || SCHEDULED_STATUS.SCHEDULED)
const timeOf = (v) => new Date(v).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false })
const formatFull = (v) => new Date(v).toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

const LOAD_ERRORS = { MAINTENANCE_NOT_FOUND: 'No encontramos este mantenimiento entre los que tienes asignados.' }
const COMPLETE_ERRORS = {
  DESCRIPTION_REQUIRED: 'Describe el trabajo realizado para poder completarlo.',
  ALREADY_COMPLETED: 'Este mantenimiento ya fue completado.',
  INVALID_IMAGE_TYPE: 'La foto debe ser JPG, PNG, WebP o HEIC.',
  IMAGE_TOO_LARGE: 'La foto no debe pasar de 8 MB.',
}

async function load() {
  try {
    item.value = await getMyScheduledDetail(scheduledId.value)
    loadError.value = ''
  } catch (err) {
    loadError.value = LOAD_ERRORS[err.message] || 'No se pudo cargar el mantenimiento.'
  } finally {
    loading.value = false
  }
}

function goBack() {
  router.push('/tecnico/calendario')
}

function onFileSelected(event) {
  photoFile.value = event.target.files?.[0] || null
}

function cancelCompleting() {
  completing.value = false
  description.value = ''
  photoFile.value = null
  completeError.value = ''
}

async function onComplete() {
  completeError.value = ''
  saving.value = true
  try {
    await completeMyScheduled(scheduledId.value, { description: description.value.trim(), file: photoFile.value })
    cancelCompleting()
    await load()
    showToast('Mantenimiento completado')
  } catch (err) {
    completeError.value = COMPLETE_ERRORS[err.message] || 'No se pudo completar el mantenimiento.'
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.ts-view { display: flex; flex-direction: column; flex: 1; min-height: 0; background: #f8f9fc; font-size: 14px; }
.ts-state { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; color: #64748b; padding: 24px; text-align: center; }
.ts-state__spinner { font-size: 1.8rem; animation: ts-spin 0.8s linear infinite; }
.ts-state__icon { font-size: 2rem; color: #94a3b8; }
.ts-state p { margin: 0; max-width: 360px; font: 400 14px Inter, sans-serif; }
@keyframes ts-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }

.ts-body { flex: 1; overflow-y: auto; padding: 12px 16px 24px; }
.ts-column { max-width: 600px; margin: 0 auto; display: flex; flex-direction: column; gap: 12px; }
.ts-block { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; }
.ts-title { margin: 0 0 8px; font-family: var(--font-display); font-weight: 700; font-size: 18px; color: #1e3a5f; line-height: 1.3; }
.ts-status { display: inline-flex; align-items: center; height: 24px; padding: 0 12px; border-radius: 12px; color: #fff; font: 600 11px Inter, sans-serif; }
.ts-description { margin: 12px 0 0; font: 400 14px Inter, sans-serif; color: #64748b; line-height: 1.5; white-space: pre-wrap; }
.ts-label { font: 500 11px Inter, sans-serif; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }

.ts-fields { display: flex; flex-direction: column; gap: 10px; }
.ts-field { display: flex; justify-content: space-between; gap: 16px; font: 500 14px Inter, sans-serif; color: #1e3a5f; text-align: right; }
.ts-field__label { font: 400 12px Inter, sans-serif; color: #94a3b8; text-align: left; }

.ts-photo { display: block; max-width: 100%; max-height: 200px; border-radius: 8px; margin-top: 12px; cursor: pointer; }
.ts-done-date { margin-top: 10px; font: 400 11px Inter, sans-serif; color: #94a3b8; }

.ts-field-form { display: flex; flex-direction: column; gap: 6px; }
.ts-field-form__label { font: 500 12px Inter, sans-serif; color: #334155; }
.ts-required { color: #dc2626; }
.ts-textarea { width: 100%; padding: 10px; border: 1px solid #e2e8f0; border-radius: 6px; font: 400 14px Inter, sans-serif; color: #334155; resize: vertical; }
.ts-textarea:focus { outline: none; border-color: #3b82f6; }
.ts-attach { display: flex; align-items: center; gap: 10px; margin-top: 10px; flex-wrap: wrap; }
.ts-attach__name { font: 400 11px Inter, sans-serif; color: #64748b; word-break: break-all; }
.ts-hidden { display: none; }
.ts-error { font: 400 12px Inter, sans-serif; color: #dc2626; margin-top: 8px; }
.ts-actions { display: flex; gap: 8px; margin-top: 12px; }

.ts-btn { height: 36px; padding: 0 14px; border-radius: 6px; cursor: pointer; font: 500 13px Inter, sans-serif; display: inline-flex; align-items: center; justify-content: center; }
.ts-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.ts-btn--primary { background: #3b82f6; border: 1px solid #3b82f6; color: #fff; }
.ts-btn--outline { background: #fff; border: 1px solid #e2e8f0; color: #334155; }
.ts-btn--block { width: 100%; }
.ts-btn--half { flex: 1; }

.ts-lightbox { position: fixed; inset: 0; z-index: 4000; background: rgba(15, 23, 42, 0.92); display: flex; align-items: center; justify-content: center; padding: 16px; }
.ts-lightbox img { max-width: 100%; max-height: 100%; object-fit: contain; border-radius: 6px; }
.ts-lightbox__close { position: absolute; top: 12px; right: 12px; width: 40px; height: 40px; border-radius: 50%; border: none; background: rgba(255, 255, 255, 0.15); color: #fff; font-size: 20px; cursor: pointer; }
.ts-toast { position: fixed; left: 50%; transform: translateX(-50%); bottom: 76px; z-index: 3000; background: #10b981; color: #fff; padding: 10px 16px; border-radius: 6px; font: 500 13px Inter, sans-serif; display: flex; align-items: center; gap: 6px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18); }

@media (max-width: 768px) {
  .ts-btn { height: 44px; }
  .ts-column { max-width: none; }
}
</style>